import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';

/**
 * Dialling restrictions.
 *
 * A policy is one shape held at three levels — account, cost centre,
 * extension — and assigned wherever it is needed. This screen edits the
 * policies; assignment happens where the thing being restricted lives.
 *
 * The country grid is the centre of it because that is the decision an
 * operator is actually making. Prefix lists sit underneath for the cases
 * countries cannot express: one supplier in a country nobody else may call,
 * or a premium range inside a country that is otherwise fine.
 */

interface Country { iso2: string; name: string; dial_prefix: string; is_high_risk: boolean }
interface Policy {
  id: string; name: string;
  allow_international: boolean;
  allowed_countries: string[];
  blocked_prefixes: string[];
  allowed_prefixes: string[];
  allow_premium: boolean;
  extension_count: number; group_count: number; tenant_count: number;
}

const useCountries = () =>
  useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<{ data: Country[] }>('/v1/countries'),
    // Reference data. It changes when the ITU changes, not when a user acts.
    staleTime: 24 * 60 * 60 * 1000,
  });

const usePolicies = (tenantId: string) =>
  useQuery({
    queryKey: ['dial-policies', tenantId],
    queryFn: () => api.get<{ data: Policy[] }>(`/v1/tenants/${tenantId}/dial-policies`),
    enabled: !!tenantId,
  });

function useSavePolicy(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/tenants/${tenantId}/dial-policies/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/dial-policies`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dial-policies', tenantId] }); },
  });
}

export function DialPolicyTab({ tenantId }: { tenantId: string }) {
  const policies = usePolicies(tenantId);
  const [editing, setEditing] = useState<Policy | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Dialling restrictions</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Which destinations may be called, and by whom.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New policy'}
        </button>
      </div>

      {policies.error && <ErrorBanner error={policies.error} />}

      {(creating || editing) && (
        <PolicyEditor tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Policy</th><th>International</th><th>Countries</th>
                <th>Lists</th><th className="arc-td-num">In use</th><th />
              </tr>
            </thead>
            {policies.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {policies.data?.data.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 550 }}>{p.name}</td>
                    <td>
                      <Badge tone={p.allow_international ? 'warn' : 'muted'}>
                        {p.allow_international ? 'on' : 'national only'}
                      </Badge>
                    </td>
                    <td className="arc-muted">
                      {p.allow_international
                        ? `${p.allowed_countries.length} permitted`
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-muted">
                      {p.blocked_prefixes.length > 0 && `${p.blocked_prefixes.length} blocked`}
                      {p.blocked_prefixes.length > 0 && p.allowed_prefixes.length > 0 && ', '}
                      {p.allowed_prefixes.length > 0 && `${p.allowed_prefixes.length} allowed`}
                      {p.blocked_prefixes.length === 0 && p.allowed_prefixes.length === 0 &&
                        <span className="arc-subtle">none</span>}
                    </td>
                    <td className="arc-td-num arc-muted">
                      {p.tenant_count + p.group_count + p.extension_count || (
                        <span className="arc-subtle">unassigned</span>
                      )}
                    </td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(p); setCreating(false); }}>Edit</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!policies.isLoading && policies.data?.data.length === 0 && (
          <Empty icon="plan" title="No dialling policies"
            hint="Without one, everybody may dial nationally and nothing international." />
        )}
      </div>
    </>
  );
}

function PolicyEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: Policy | null; onDone: () => void;
}) {
  const save = useSavePolicy(tenantId);
  const countries = useCountries();

  const [name, setName] = useState(existing?.name ?? '');
  const [intl, setIntl] = useState(existing?.allow_international ?? false);
  const [premium, setPremium] = useState(existing?.allow_premium ?? false);
  const [picked, setPicked] = useState<Set<string>>(new Set(existing?.allowed_countries ?? []));
  const [blocked, setBlocked] = useState((existing?.blocked_prefixes ?? []).join('\n'));
  const [allowed, setAllowed] = useState((existing?.allowed_prefixes ?? []).join('\n'));
  const [search, setSearch] = useState('');

  const list = countries.data?.data ?? [];
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      c.name.toLowerCase().includes(q) || c.dial_prefix.startsWith(q.replace(/\D/g, '')));
  }, [list, search]);

  const toggle = (iso2: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(iso2)) n.delete(iso2); else n.add(iso2);
      return n;
    });

  const riskyPicked = list.filter((c) => c.is_high_risk && picked.has(c.iso2));
  const err = save.error instanceof ApiClientError ? save.error : null;
  const lines = (v: string) => v.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name,
          allow_international: intl,
          allowed_countries: [...picked],
          blocked_prefixes: lines(blocked),
          allowed_prefixes: lines(allowed),
          allow_premium: premium,
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New dialling policy'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div className="arc-field" style={{ maxWidth: 340 }}>
        <label className="arc-label" htmlFor="dp-name">Name</label>
        <input id="dp-name" className="arc-input" required value={name}
          onChange={(e) => setName(e.target.value)} placeholder="Sales — international" />
      </div>

      <div className="arc-row" style={{ gap: 10, marginBottom: 6 }}>
        <Switch id="dp-intl" checked={intl} onChange={setIntl} />
        <label htmlFor="dp-intl" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Allow international calling
          <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
            Off means national only, whatever the country list says.
          </span>
        </label>
      </div>

      {intl && (
        <>
          <div className="arc-banner arc-banner-info" style={{ margin: '12px 0' }}>
            <Icon name="info" size={14} />
            <span>
              Enable only countries you actually call. Every country left off is one
              a compromised handset cannot dial — this list is the main defence
              against toll fraud, not a convenience.
            </span>
          </div>

          <div className="arc-row" style={{ marginBottom: 8 }}>
            <div className="arc-search" style={{ maxWidth: 320 }}>
              <Icon name="search" size={14} />
              <input className="arc-input" placeholder="Search country or dial code"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="arc-spacer" />
            <span className="arc-muted" style={{ fontSize: 12 }}>
              {picked.size} of {list.length} enabled
            </span>
            {picked.size > 0 && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setPicked(new Set())}>Clear all</button>
            )}
          </div>

          {countries.isLoading
            ? <div className="arc-skeleton" style={{ height: 220 }} />
            : (
              <div className="arc-country-grid">
                {shown.map((c) => (
                  <label key={c.iso2} className="arc-country" data-on={picked.has(c.iso2)}>
                    <span className="arc-country-code">+{c.dial_prefix}</span>
                    <span className="arc-truncate" style={{ flex: 1 }}>{c.name}</span>
                    {/* Flagged rather than blocked: it is policy, and policy
                        belongs somewhere an operator can see and change it. */}
                    {c.is_high_risk && (
                      <span title="Commonly abused for toll fraud">
                        <Icon name="warning" size={12} />
                      </span>
                    )}
                    <Switch checked={picked.has(c.iso2)} label={c.name}
                      onChange={() => toggle(c.iso2)} />
                  </label>
                ))}
              </div>
            )}

          {shown.length === 0 && !countries.isLoading && (
            <p className="arc-muted" style={{ fontSize: 12.5, padding: '10px 0' }}>
              No country matches “{search}”.
            </p>
          )}

          {riskyPicked.length > 0 && (
            <div className="arc-banner arc-banner-warn" style={{ marginTop: 12 }}>
              <Icon name="warning" size={14} />
              <span>
                {riskyPicked.map((c) => c.name).join(', ')}{' '}
                {riskyPicked.length === 1 ? 'is' : 'are'} commonly used for toll fraud.
                Leave enabled only if somebody genuinely calls there.
              </span>
            </div>
          )}
        </>
      )}

      <div className="arc-grid arc-grid-2" style={{ marginTop: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dp-block">Blocked prefixes</label>
          <textarea id="dp-block" className="arc-input" rows={4} value={blocked}
            onChange={(e) => setBlocked(e.target.value)}
            placeholder={'27861\n882'} style={{ fontFamily: 'var(--arc-mono)', fontSize: 12 }} />
          {/* Stated because the precedence is the whole point: a block that an
              allow could override would not be a block. */}
          <p className="arc-hint">
            One per line, without the +. Beats everything, including the country list.
          </p>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dp-allow">Allowed prefixes</label>
          <textarea id="dp-allow" className="arc-input" rows={4} value={allowed}
            onChange={(e) => setAllowed(e.target.value)}
            placeholder="442071234" style={{ fontFamily: 'var(--arc-mono)', fontSize: 12 }} />
          <p className="arc-hint">
            Reaches a destination the country rules would refuse — one supplier
            in a country nobody else may call.
          </p>
        </div>
      </div>

      <div className="arc-row" style={{ gap: 10, marginTop: 10, marginBottom: 14 }}>
        <Switch id="dp-prem" checked={premium} onChange={setPremium} />
        <label htmlFor="dp-prem" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Allow high-risk destinations
        </label>
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : existing ? 'Save policy' : 'Create policy'}
      </button>
    </form>
  );
}
